import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Leaf } from 'lucide-react';
import { Button } from './ui/button';

export const Header = ({ onBookClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBookAppointment = () => {
    if (onBookClick) {
      onBookClick();
    } else {
      window.location.href = 'tel:+919986671810';
    }
  };

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/#about' },
    { name: 'Treatments', href: '/#treatments' },
    { name: 'Articles', href: '/articles' },
    { name: 'Reviews', href: '/#reviews' },
    { name: 'Contact', href: '/#contact' },
  ];

  const scrollToSection = (href) => {
    if (href.startsWith('/#')) {
      const sectionId = href.replace('/#', '');
      if (location.pathname !== '/') {
        window.location.href = href;
      } else {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/90 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="section-container">
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-2 group"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_54c61862-94c1-4446-b49b-c57faa7a3dc2/artifacts/pmn8bqyt_logo%20rm.jpg.jpeg"
              alt="AyurvedMed Healthcare"
              className="h-24 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              link.href.startsWith('/#') ? (
                <button
                  key={link.name}
                  onClick={() => scrollToSection(link.href)}
                  className="text-[#2D4A3E] hover:text-[#C67D63] font-medium transition-colors duration-200"
                  data-testid={`nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </button>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-[#2D4A3E] hover:text-[#C67D63] font-medium transition-colors duration-200"
                  data-testid={`nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <Button
              onClick={handleBookAppointment}
              data-testid="header-book-btn"
              className="bg-[#2D4A3E] hover:bg-[#1e3329] text-white px-6 py-2.5 rounded-full flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
            >
              <Phone className="w-4 h-4" />
              Book Appointment
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-[#2D4A3E]"
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed inset-0 top-[72px] bg-white/95 backdrop-blur-md z-40 transition-transform duration-300 ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="mobile-menu"
      >
        <nav className="flex flex-col p-6 gap-4">
          {navLinks.map((link) => (
            link.href.startsWith('/#') ? (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className="text-lg text-[#2D4A3E] hover:text-[#C67D63] font-medium py-2 text-left transition-colors duration-200"
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </button>
            ) : (
              <Link
                key={link.name}
                to={link.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-lg text-[#2D4A3E] hover:text-[#C67D63] font-medium py-2 transition-colors duration-200"
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            )
          ))}
          <Button
            onClick={handleBookAppointment}
            data-testid="mobile-book-btn"
            className="mt-4 bg-[#2D4A3E] hover:bg-[#1e3329] text-white px-6 py-3 rounded-full flex items-center justify-center gap-2"
          >
            <Phone className="w-4 h-4" />
            Book Appointment
          </Button>
        </nav>
      </div>
    </header>
  );
};

export default Header;
